import React from 'react';
import ProjectedPAIScore from '../types/ProjectedPAIScore';
import clsx from 'clsx';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, Theme } from '@material-ui/core/styles';
import NotAvailable from './NotAvailable';
import CheckIcon from '../icons/CheckIcon';
import Tooltip from './Tooltip';
import {
  COLOR_GREEN,
  COLOR_ORANGE,
  COLOR_TEXT,
  COLOR_MIDDLE_GRAY,
  COLOR_BLUE,
  COLOR_PURPLE,
  COLOR_DARK_GRAY,
} from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import PAIScore from '../types/PAIScore';
import CombineScore from '../types/CombineScore';
import PPIScore from '../types/PPIScore';
import Position from '../types/Position';
import HighSchoolCombine from '../types/HighSchoolCombine';
import NFLCombine from '../types/NFLCombine';
import ProCombineScore from '../types/ProCombineScore';
import { RanksType } from './Ranks';
import { getScoreTooltip } from '../services/score';

interface LabledScoreProps {
  className?: string;
  noScoreTextClass?: string;
  checkIconClass?: string;
  type: string;
  scoreList?: Array<PAIScore | CombineScore | ProjectedPAIScore>; 
  value?: number;
  position?: Position;
  combines?: Array<HighSchoolCombine | NFLCombine>;
  totalValue?: number;
  showCheckMarkExplict?: boolean;
  isNotHsMultiSport?: boolean;
  multiSportTooltip?: React.ReactNode;
  label?: string;
  isHSMPH?: boolean;
  selectedYear?: number;
  onClick?: () => void;
}

export const SCORE_TYPE = {
  PAI: 'pai',
  PPAI: 'ppai',
  COMBINE: 'combine',
  PRO_COMBINE: 'proCombine',
  PPI: 'ppi',
  MPH: 'mph',
  MULTISPORTS: 'multiSports'
};

const useStyles = makeStyles((theme: Theme) => ({
  score: {
    width: '80px',
    height: '80px',
    position: 'relative',
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 600,

    '&::before': {
      content: '""',
      width: 'calc(100% - 1px)',
      height: 'calc(100% - 1px)',
      position: 'absolute',
      top: '1px',
      left: '1px',
      border: `5px solid ${COLOR_MIDDLE_GRAY}`,
      borderRadius: '50%',
    },
    
    [theme.breakpoints.down('xs')]: {
      width: '70px',
      height: '70px',
      fontSize: theme.typography.pxToRem(22),

      '&::before': {
        top: '2px',
        left: '2px',
        width: 'calc(100% - 4px)',
        height: 'calc(100% - 4px)',
        border: `2px solid ${COLOR_MIDDLE_GRAY}`,
      },
    },
  },
  noScore: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '&$pai::before': {
      borderColor: COLOR_ORANGE,
    },

    '&$ppai::before': {
      borderStyle: 'solid',
      borderColor: COLOR_ORANGE,
    },

    '&$combine::before': {
      borderColor: COLOR_GREEN,
    },

    '&$ppi::before': {
      borderColor: COLOR_BLUE,
    },

    '&$mph::before': {
      borderColor: COLOR_PURPLE,
    },

    '&$proCombine::before': {
      borderColor: COLOR_TEXT,
    },
  },
  pai: {
    '& $progress': {
      color: COLOR_ORANGE,
    },
  },
  ppai: {
    '& $progress': {
      color: COLOR_ORANGE,
    },

    '&::before': {
      border: `5px dashed ${COLOR_ORANGE}`,
    },

    [theme.breakpoints.down('xs')]: {
      '&::before': {
        border: `2px dashed ${COLOR_ORANGE}`,
      },
    },
  },
  combine: {
    '& $progress': {
      color: COLOR_GREEN,
    },
  },
  ppi: {
    '& $progress': {
      color: COLOR_BLUE,
    },
  },
  mph: {
    '& $progress': {
      color: COLOR_PURPLE,
    },
  },
  proCombine: {
    '& $progress': {
      color: COLOR_TEXT,
    },
  },
  multiSports: {
    '& $progress': {
      color: COLOR_MIDDLE_GRAY,
    },
  },
  withProCombineCheckMark: {
    '& $checkIcon': {
      color: COLOR_TEXT,
    },
  },
  progress: {
    width: '100% !important',
    height: '100% !important',

    '& circle': {
      strokeLinecap: 'round',
    },
    
    [theme.breakpoints.down('xs')]: {
      '& circle': {
        strokeWidth: 3,
      },
    },
  },
  scoreValue: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 1,
    fontSize: 'inherit',
    fontWeight: 'bold',
    color: COLOR_TEXT,
  },
  checkIcon: {
    height: '16px',
    width: '16px',
    color: COLOR_GREEN,
  },
  noScoreText: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 600,
    color: COLOR_TEXT,
  },
  scoreLabel: {
    color: COLOR_BLUE,
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    lineHeight: 1,
    marginTop: 2,
    textTransform: 'uppercase',
    textDecoration: 'underline',
  },
  noScoreLabel: {
    color: COLOR_DARK_GRAY,
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    lineHeight: 1,
    marginTop: 2,
    textTransform: 'uppercase',
  },
  scoreValueColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}), { name: LabledScore.name });

export function getScoreObj (
  scoreList: Array<ProjectedPAIScore | PAIScore | CombineScore | PPIScore | ProCombineScore>,
  { position }:{
    position?: Position;
  } = {},
): PAIScore | CombineScore | PPIScore | ProCombineScore | ProjectedPAIScore | undefined {
  const score = position
    ? ((scoreList || []).find(score => score && score.code === position))
    : ((scoreList || []).find(score => score && score.isPrimary));

  return score;
}

export function getScoreValue (
  scoreList: Array<PAIScore | CombineScore | PPIScore | ProCombineScore | ProjectedPAIScore>,
  { position, combines }:{
    position?: Position;
    combines?: Array<HighSchoolCombine | NFLCombine>;
  } = {},
):number|null {
  const score = position
    ? ((scoreList || []).find(score => score && score.code === position)) || {}
    : ((scoreList || []).find(score => score && score.isPrimary)) || {};

  const hasCombineDataButNoValue = combines?.length
    && (!scoreList || !scoreList.length);

  if (typeof (score as PAIScore).pai === 'number') {
    return (score as PAIScore).pai;
  } else if (typeof (score as CombineScore).combine === 'number') {
    return (score as CombineScore).combine;
  } else if (typeof (score as ProCombineScore).proCombine === 'number') {
    return (score as ProCombineScore).proCombine;
  } else if (typeof (score as PPIScore).ppi === 'number') {
    return (score as PPIScore).ppi;
  } else if (typeof (score as ProjectedPAIScore).projectedPai === 'number') {
    return (score as ProjectedPAIScore).projectedPai;
  } else {
    return hasCombineDataButNoValue ? 0.001 : null;
  }
}

export function getProCombineScoreValue (
  nflCombineScore: number | null,
  nflCombines: Array<NFLCombine>
):number|null {
  const hasProCombineDataButNoValue = nflCombines?.length && !nflCombineScore;

  return nflCombineScore
    ? nflCombineScore
    : hasProCombineDataButNoValue ? 0.001 : null;
}

export default function LabledScore (props:LabledScoreProps) {
  const {
    className,
    noScoreTextClass,
    checkIconClass,
    type,
    scoreList = [],
    value,
    position,
    combines = [],
    totalValue = 5,
    showCheckMarkExplict = false,
    isNotHsMultiSport = true,
    multiSportTooltip,
    label,
    isHSMPH = false,
    selectedYear,
    onClick,
  } = props;
  const classes = useStyles();

  const scoreValue:number|null = value || (position ? getScoreValue(scoreList, { position }) : getScoreValue(scoreList));
  const scoreObj = position ? getScoreObj(scoreList, { position }) : getScoreObj(scoreList);
  const validScore = typeof scoreValue === 'number' && scoreValue >= 0;
  const roundedScore = validScore
    ? (Math.round((scoreValue as number) * 10) / 10).toFixed(1)
    : 0;
  const showCombineCheckMark = (type === SCORE_TYPE.COMBINE
    && !!combines?.length
    && !scoreList?.length) || showCheckMarkExplict;
  const showProCombineCheckMark = type === SCORE_TYPE.PRO_COMBINE
    && !!combines?.length
    && !value;
  const showCheckMark = showCombineCheckMark || showProCombineCheckMark;
  const showProgressCircle = validScore && type !== SCORE_TYPE.PPAI;
  const isClickable = !!onClick && validScore;
  let checkmarkTooltipText = '';
  if (type === SCORE_TYPE.COMBINE) {
    checkmarkTooltipText = 'Attended verified HS combine no calculated score';
  } else if (type === SCORE_TYPE.PRO_COMBINE) {
    checkmarkTooltipText = 'Attended NFL Combine or Pro Day no calculated score';
  }
  let confidenceCoefficient;
  if (type === SCORE_TYPE.PPAI) {
    const projectedScore = scoreObj as ProjectedPAIScore | undefined;
    confidenceCoefficient = projectedScore?.upperBound && projectedScore?.lowerBound
      ? (projectedScore.upperBound - projectedScore.lowerBound)
      : undefined;
  }
  const tooltipText = multiSportTooltip ? multiSportTooltip : getScoreTooltip(type as RanksType, scoreValue as number, {isHSMPH, year: selectedYear, confidenceCoefficient});

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isClickable) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      className={clsx(
        classes.score,
        !validScore && classes.noScore,
        {
          [classes.pai]: type === SCORE_TYPE.PAI,
          [classes.ppai]: type === SCORE_TYPE.PPAI,
          [classes.combine]: type === SCORE_TYPE.COMBINE,
          [classes.ppi]: type === SCORE_TYPE.PPI,
          [classes.proCombine]: type === SCORE_TYPE.PRO_COMBINE,
          [classes.mph]: type === SCORE_TYPE.MPH,
          [classes.multiSports]: type === SCORE_TYPE.MULTISPORTS,
          [classes.withProCombineCheckMark]: showProCombineCheckMark,
        },
        className,
      )}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable && label ? `Open ${label} section` : undefined}
    >
      {(!validScore && !showCheckMark) && (
        isNotHsMultiSport ?
        <>
          <div className={clsx(classes.scoreValue, label && classes.scoreValueColumn)} style={{cursor: 'pointer'}}>
            {/* {Math.floor(scoreValue as number)} */}
            <NotAvailable className={clsx(classes.noScoreText, noScoreTextClass)} />
            {label && <div className={classes.noScoreLabel}>{label == 'PPAI' ? 'PAI' : label}</div>}
          </div>
        </>
        : <div>{0}</div>
      )}

      {validScore && (
        <>
          {showProgressCircle && (
            <CircularProgress
              classes={{
                root: classes.progress,
              }}
              variant='determinate'
              value={Number(((scoreValue as number) * 100 / totalValue).toFixed(2))}
              thickness={4}
            />
          )}

          {isNotHsMultiSport ? 
            <Tooltip title={tooltipText}>
              <div className={clsx(classes.scoreValue, label && classes.scoreValueColumn)}>
                <div>{multiSportTooltip ? Math.floor(scoreValue as number) : roundedScore}</div>
                {label && <div className={classes.scoreLabel}>{label}</div>}
              </div>
            </Tooltip>
            : <div className={clsx(classes.scoreValue, label && classes.scoreValueColumn)} style={{cursor: 'pointer'}}>
              {Math.floor(scoreValue as number)}
              {label && <div className={classes.scoreLabel}>{label}</div>}
            </div>
          }
        </>
      )}

      {(!validScore && !!showCheckMark) && (
        <>
          <CircularProgress
            classes={{
              root: classes.progress,
            }}
            variant='determinate'
            value={100}
            thickness={4}
          />

        <Tooltip title={checkmarkTooltipText}>
          <div className={clsx(classes.scoreValue, label && classes.scoreValueColumn)}>
            <CheckIcon className={clsx(classes.checkIcon, checkIconClass)} />
            {label && <div className={classes.scoreLabel}>{label}</div>}
          </div>
        </Tooltip>
        </>
      )}
    </div>
  );
}
